import React, { forwardRef, useRef, useState } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faCheck,
  faChevronDown,
  faChevronRight,
  faClose,
  faSpinner,
  faTimes,
  faWarning,
} from "@fortawesome/pro-regular-svg-icons";
import { faSearch } from "@fortawesome/pro-solid-svg-icons";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { AnimatePresence, m } from "framer-motion";
import { IFuseOptions } from "fuse.js";
import { twJoin, twMerge } from "tailwind-merge";

import { LOCK_NODE_EDITOR_CLASSNAME } from "src/authoringMultiplayerLock/constants";
import { Input } from "src/base-components/Input";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { Pill, PillVariants } from "src/base-components/Pill";
import { Icon } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { Tooltip } from "src/design-system/Tooltip";
import { FIELD_BORDER, FIELD_TEXT } from "src/design-system/utils/fieldStyles";
import { HeadlessUIPlacement } from "src/utils/headlessui";
import { useFuseSearch } from "src/utils/useFuseSearch";

type SectionCommon = {
  key: string;
  // Subsections must have a display value
  displayValue: React.ReactNode;
};

// eslint-disable-next-line sonarjs/redundant-type-aliases
export type Subsection = SectionCommon;

export type Section = Omit<SectionCommon, "displayValue"> & {
  // We support sections with no section header
  displayValue: React.ReactNode | null;
  subsections?: Subsection[];
};

export type Option = {
  key: string;
  value: React.ReactNode;
  // Alternative text shown in selected state (pills). Falls back to 'value' if undefined.
  displayValue?: React.ReactNode;
  disabled?: boolean | string;
  //Separate prop needed as Combobox shares Option type and expects disabled: boolean | string only
  disabledTooltip?: React.ReactNode;
  searchText?: string;
  icon?: IconProp;
  sectionKey?: string;
  description?: string;
  // Variant of the pill shown in selected state (pills). Falls back to gray pill if not provided.
  pillVariant?: PillVariants;
};

type CommonProps = {
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  placement?: HeadlessUIPlacement;
  errored?: boolean;
  hinted?: boolean;
  errorTooltip?: React.ReactNode;
  dataLoc?: string;
  dropdownPlaceholder?: React.ReactNode;
  fullWidth?: boolean;
  useLockNodeEditorClassName?: boolean;
  compact?: boolean;
  optionsFitContent?: boolean;
  sections?: Section[];
  sectionDividers?: boolean;
};

type MultipleSelectionProps = {
  multiple: true;
  onChange?: (value: string[]) => void;
  value: string[];
  showSelectAllAsOption?: boolean;
  selectAllOption?: Omit<Option, "key">;
  showResetButton?: boolean;
};

type SingleSelectionProps = {
  multiple?: false;
  value?: Nullable<string>;
  buttonRenderer?: (option?: Option) => React.ReactNode;
} & (
  | { showResetButton?: false | undefined; onChange?: (value: string) => void }
  | { showResetButton: true; onChange?: (value: Nullable<string>) => void }
);

type SearchProps =
  | { searchable?: false }
  | {
      searchable: true;
      searchOptions?: IFuseOptions<Option>;
      searchPlaceholder?: string;
      clearAfterClose?: boolean;
    };

type SelectProps = CommonProps &
  SearchProps &
  (MultipleSelectionProps | SingleSelectionProps);

const getSearchKeys = (props: SelectProps) => {
  if (props.searchable && props.searchOptions?.keys) {
    return props.searchOptions.keys;
  }

  const hasSearchText = props.options[0]?.searchText;

  return hasSearchText ? ["searchText"] : ["value"];
};

export const Select: React.FC<SelectProps> = (propsUnfiltered) => {
  const [query, setQuery] = useState("");
  const searchOptions = propsUnfiltered.searchable
    ? (propsUnfiltered.searchOptions ?? {})
    : {};

  const search = useFuseSearch(propsUnfiltered.options, {
    threshold: 0.3,
    ...searchOptions,
    keys: getSearchKeys(propsUnfiltered),
  });
  const searchFilteredOptions = search(query);

  const onChange = (value: Nullable<string> | string[]) => {
    if (propsUnfiltered.multiple) {
      propsUnfiltered.onChange?.(value as string[]);
    } else {
      propsUnfiltered.onChange?.(value as string);
    }
    selectRef.current?.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const props = {
    ...propsUnfiltered,
    options: searchFilteredOptions,
    onChange,
  };

  const selectRef = useRef<HTMLSelectElement | null>(null);

  return (
    <>
      {/* Hidden native select element as workaround for
       * headless ui not using it and therefore not triggering
       * native events such as onChange in a form
       * https://github.com/tailwindlabs/headlessui/issues/2003
       */}
      <select ref={selectRef} style={{ display: "none" }} />
      <Listbox
        disabled={props.disabled}
        multiple={props.multiple}
        value={props.value}
        onChange={onChange}
      >
        {({ open }) => (
          <>
            <div className={twJoin("flex", props.fullWidth && "w-full")}>
              <ListboxButton className="w-full" data-loc={props.dataLoc}>
                <SelectButton
                  {...propsUnfiltered}
                  open={open}
                  onChange={onChange}
                />
              </ListboxButton>
            </div>
            <AnimatePresence
              initial={false}
              onExitComplete={() => {
                if (props.searchable && props.clearAfterClose) {
                  setQuery("");
                }
              }}
            >
              {open && (
                <SelectOptions {...props} query={query} setQuery={setQuery} />
              )}
            </AnimatePresence>
          </>
        )}
      </Listbox>
    </>
  );
};

const SelectButton: React.FC<SelectProps & { open: boolean }> = (props) => {
  const className = twMerge(
    "flex justify-between rounded-lg border border-gray-200 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/25",
    props.compact
      ? "min-h-[24px] py-0.5 pr-1 pl-2"
      : "min-h-[32px] py-1 pr-2 pl-3",
    props.options.length === 0 && "cursor-default",
    !props.errored && props.hinted && FIELD_BORDER.hinted,
    props.errored && FIELD_BORDER.errored,
    props.open && "border-indigo-400 ring-2 ring-indigo-500/25",
    props.disabled ? "cursor-not-allowed bg-gray-50" : "bg-white",
  );

  const isValueSelected =
    (props.multiple && props.value.length > 0) ||
    (!props.multiple && props.value);

  const placeholderElement = (
    <span className="font-inter-normal-12px text-gray-500">
      {props.placeholder}
    </span>
  );

  const handleRemove = (removedOption: Option) => {
    if (props.multiple) {
      props.onChange?.(props.value.filter((v) => v !== removedOption.key));
    }
  };

  const onReset = () => {
    if (props.showResetButton) {
      if (props.multiple) {
        props.onChange?.([]);
      } else {
        props.onChange?.(null);
      }
    }
  };

  const selectedValuesElement = (() => {
    if (props.multiple) {
      return (
        <div
          className="flex min-w-0 flex-1 flex-wrap gap-1"
          data-loc={props.dataLoc && `${props.dataLoc}-values`}
        >
          {props.value
            .map((key) => props.options.find((option) => option.key === key))
            .map((option, index) =>
              option ? (
                <Pill
                  key={option.key}
                  size="sm"
                  variant={
                    option.pillVariant ??
                    (props.hinted && !props.errored ? "indigo-light" : "gray")
                  }
                  maxWidth
                >
                  {option.icon && <Pill.Icon icon={option.icon} />}
                  <Pill.Text>{option.displayValue ?? option.value}</Pill.Text>
                  {!props.disabled && !option.disabled && (
                    <div
                      className="flex items-center opacity-60 hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(option);
                      }}
                    >
                      <Pill.Icon icon={faClose} />
                    </div>
                  )}
                </Pill>
              ) : (
                <Skeleton
                  key={`skeleton-${index}`}
                  height="h-5.5"
                  width="w-24"
                />
              ),
            )}
        </div>
      );
    }

    const singleSelectTextColor = props.disabled
      ? FIELD_TEXT.disabled
      : props.hinted && !props.errored
        ? FIELD_TEXT.hinted
        : FIELD_TEXT.default;

    return props.buttonRenderer ? (
      <div
        className={twJoin(
          "min-w-0 flex-1 self-center text-left font-inter-normal-12px",
          singleSelectTextColor,
        )}
      >
        {props.buttonRenderer(
          props.options.find((option) => option.key === props.value)!,
        )}
      </div>
    ) : (
      <div
        className={twJoin(
          "min-w-0 flex-1 self-center text-left font-inter-normal-12px",
          singleSelectTextColor,
        )}
      >
        {props.options.find((option) => option.key === props.value)?.value}
      </div>
    );
  })();

  const resetButtonAvailable = props.showResetButton && isValueSelected;

  return (
    <div
      className={twJoin(
        "flex items-center",
        props.compact ? "gap-1" : "gap-2.5",
        className,
      )}
    >
      {isValueSelected ? selectedValuesElement : placeholderElement}

      <div
        className={twJoin(
          "flex self-start",
          props.compact ? "pt-0.5" : "pt-px",
        )}
      >
        <Icon
          dataLoc={
            props.loading
              ? `${props.dataLoc}-options-loading-icon`
              : `${props.dataLoc}-toggle-dropdown`
          }
          icon={props.loading ? faSpinner : faChevronDown}
          size={props.compact ? "3xs" : "xs"}
          spin={props.loading}
        />
        {props.errored && props.errorTooltip && (
          <Tooltip
            title={props.errorTooltip}
            asChild
            disabled={!props.errorTooltip}
          >
            <span
              data-loc={props.dataLoc && `${props.dataLoc}-error-tooltip-icon`}
            >
              <Icon icon={faWarning} size="xs" variant="error" />
            </span>
          </Tooltip>
        )}
        {resetButtonAvailable && (
          <div
            className="flex items-center justify-start"
            data-loc={props.dataLoc && `${props.dataLoc}-reset`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-1.5 h-4 w-[1px] bg-gray-200" />
            <div role="button" onClick={onReset}>
              <Icon icon={faTimes} size="xs" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const selectableOptions = (options: Option[]) =>
  options.filter((option) => option.key !== SELECT_DIVIDER.key);

type RenderOptionProps = {
  option: Option;
  index: number;
  compact?: boolean;
};

const RenderOption: React.FC<RenderOptionProps> = ({
  option,
  index,
  compact,
}) => (
  <ListboxOption
    data-loc={`select-option-${index}`}
    disabled={!!option.disabled}
    value={option.key}
  >
    {({ selected }) => (
      <Tooltip
        body={option.disabledTooltip || option.disabled}
        disabled={
          !option.disabledTooltip && typeof option.disabled !== "string"
        }
        placement="right"
        asChild
      >
        <button
          className={twMerge(
            "flex w-full cursor-pointer items-start justify-between gap-x-2",
            compact
              ? "px-3 py-2 font-inter-normal-12px"
              : "px-4 py-3.5 font-inter-normal-13px",
            "ui-active:bg-gray-50",
            option.disabled
              ? "cursor-default text-gray-400"
              : "text-gray-800 hover:bg-gray-50",
          )}
        >
          {option.icon && <Icon icon={option.icon} size="xs" />}
          {option.description ? (
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="text-start">{option.value}</div>
              {option.description && (
                <div className="text-start font-inter-normal-12px text-gray-500">
                  {option.description}
                </div>
              )}
            </div>
          ) : (
            <div className="min-w-0 flex-1 text-start">{option.value}</div>
          )}
          {selected && (
            <div>
              <Icon icon={faCheck} size="xs" variant="active" />
            </div>
          )}
        </button>
      </Tooltip>
    )}
  </ListboxOption>
);

type SubsectionHeaderProps = {
  subsection: Subsection;
  isExpanded: boolean;
  onToggle: () => void;
  compact?: boolean;
  dataLoc?: string;
};

const SubsectionHeader: React.FC<SubsectionHeaderProps> = ({
  subsection,
  isExpanded,
  onToggle,
  compact,
  dataLoc,
}) => (
  <div
    className={twJoin(
      "flex cursor-pointer items-center gap-x-2",
      compact ? "px-3 py-2" : "px-4 py-3.5",
      "hover:bg-gray-50",
    )}
    data-loc={dataLoc}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onToggle();
    }}
  >
    <div
      className={twJoin(
        "transition-transform duration-150",
        isExpanded && "rotate-90",
      )}
    >
      <Icon icon={faChevronRight} size="xs" />
    </div>
    <span className="font-inter-medium-13px text-gray-800">
      {subsection.displayValue}
    </span>
  </div>
);

type ResultsWithSectionsProps = {
  options: Option[];
  sections: Section[];
  compact?: boolean;
  dataLoc?: string;
  sectionDividers?: boolean;
  isSearchingResults: boolean;
};

const ResultsWithSections: React.FC<ResultsWithSectionsProps> = ({
  options,
  sections,
  compact,
  dataLoc,
  sectionDividers,
  isSearchingResults,
}) => {
  const [expandedSubsections, setExpandedSubsections] = useState<
    Record<string, boolean>
  >({});

  const toggleSubsection = (subsectionKey: string) => {
    setExpandedSubsections((prev) => ({
      ...prev,
      [subsectionKey]: !prev[subsectionKey],
    }));
  };

  // Build mapping: subsectionKey → parentSectionKey
  const subsectionToSection = new Map<string, string>();
  sections.forEach((section) => {
    section.subsections?.forEach((sub) => {
      subsectionToSection.set(sub.key, section.key);
    });
  });

  // Group options by their actual parent section
  const optionsBySection = new Map<string, Option[]>();
  const optionsBySubsection = new Map<string, Option[]>();

  options.forEach((option) => {
    const sectionKey = option.sectionKey ?? "";
    if (subsectionToSection.has(sectionKey)) {
      // Option belongs to a subsection
      const existing = optionsBySubsection.get(sectionKey) ?? [];
      optionsBySubsection.set(sectionKey, [...existing, option]);
    } else {
      // Option belongs directly to a section
      const existing = optionsBySection.get(sectionKey) ?? [];
      optionsBySection.set(sectionKey, [...existing, option]);
    }
  });

  // Filter sections that have content (direct options or subsections with options)
  const sectionsWithResults = sections
    .filter((section) => {
      const hasDirectOptions = optionsBySection.has(section.key);
      const hasSubsectionOptions = section.subsections?.some((sub) =>
        optionsBySubsection.has(sub.key),
      );
      return hasDirectOptions || hasSubsectionOptions;
    })
    .map((section) => ({
      ...section,
      directResults: optionsBySection.get(section.key) ?? [],
      subsectionsWithResults:
        section.subsections
          ?.filter((sub) => optionsBySubsection.has(sub.key))
          .map((sub) => ({
            ...sub,
            results: optionsBySubsection.get(sub.key) ?? [],
          })) ?? [],
    }));

  return (
    <>
      {sectionsWithResults.map((section, sectionIndex) => (
        <div
          key={section.key}
          data-loc={dataLoc && `${dataLoc}-section-${section.key}`}
        >
          {sectionDividers && sectionIndex > 0 && <Divider />}
          {/* We support sections with no section header */}
          {section.displayValue !== null && (
            <div className="pb-1.5 pl-4">
              {typeof section.displayValue === "string" ? (
                <p className="font-inter-semibold-13px text-gray-800">
                  {section.displayValue}
                </p>
              ) : (
                section.displayValue
              )}
            </div>
          )}

          {/* Direct options under section (no subsection) */}
          {section.directResults.map((option, optionIndex) =>
            option === SELECT_DIVIDER ? (
              <Divider key={`${option.key}-${optionIndex}`} />
            ) : (
              <RenderOption
                key={option.key}
                compact={compact}
                index={optionIndex}
                option={option}
              />
            ),
          )}

          {/* Subsections with collapse/expand */}
          {section.subsectionsWithResults.map((subsection) => {
            const isExpanded =
              isSearchingResults || expandedSubsections[subsection.key];

            return (
              <div key={subsection.key}>
                <SubsectionHeader
                  compact={compact}
                  dataLoc={dataLoc && `${dataLoc}-subsection-${subsection.key}`}
                  isExpanded={isExpanded}
                  subsection={subsection}
                  onToggle={() => toggleSubsection(subsection.key)}
                />
                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <m.div
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      initial={{ height: 0, opacity: 0 }}
                      style={{ overflow: "hidden" }}
                      transition={{ duration: 0.15 }}
                    >
                      {subsection.results.map((option, optionIndex) =>
                        option === SELECT_DIVIDER ? (
                          <Divider key={`${option.key}-${optionIndex}`} />
                        ) : (
                          <RenderOption
                            key={option.key}
                            compact={compact}
                            index={optionIndex}
                            option={option}
                          />
                        ),
                      )}
                    </m.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
};

const SelectOptions = forwardRef<
  HTMLUListElement,
  SelectProps & { query: string; setQuery: (q: string) => void }
>((props, ref) => {
  const allSelected =
    !!props.multiple &&
    props.value.length === selectableOptions(props.options).length;
  return (
    <ListboxOptions
      ref={ref}
      anchor={{
        to: props.placement ?? "bottom start",
        gap: 8,
        padding: 8,
      }}
      as={MotionDropdownPanel}
      className={twJoin(
        "w-[var(--button-width)] min-w-[160px] outline-none",
        props.optionsFitContent && "w-fit min-w-[var(--button-width)]",
        props.useLockNodeEditorClassName && LOCK_NODE_EDITOR_CLASSNAME,
      )}
      data-loc={`${props.dataLoc}-options`}
      static
    >
      {props.searchable && (
        <div className="px-4 py-2">
          <Input
            ref={(node) => node?.focus()} //Focus on first render
            data-loc="select-input"
            placeholder={props.searchPlaceholder}
            prefixIcon={{ icon: faSearch }}
            value={props.query}
            fullWidth
            onChange={(e) => props.setQuery(e.target.value)}
          />
        </div>
      )}
      <div className="decideScrollbar max-h-80 overflow-auto">
        {props.multiple && props.showSelectAllAsOption && (
          <>
            <div
              className="flex cursor-pointer justify-between px-4 py-3.5 font-inter-normal-12px text-gray-800 hover:bg-gray-50"
              onClick={() =>
                props.onChange?.(
                  allSelected
                    ? []
                    : selectableOptions(props.options).map(
                        (option) => option.key,
                      ),
                )
              }
            >
              <span className="flex items-center gap-x-2">
                {props.selectAllOption?.icon && (
                  <Icon icon={props.selectAllOption.icon} size="xs" />
                )}
                {props.selectAllOption?.value ?? "All"}
              </span>
              {allSelected && (
                <span>
                  <Icon icon={faCheck} size="sm" variant="active" />
                </span>
              )}
            </div>
            <Divider />
          </>
        )}
        {props.sections ? (
          <ResultsWithSections
            compact={props.compact}
            dataLoc={props.dataLoc}
            isSearchingResults={Boolean(props.query && props.query.length > 0)}
            options={props.options}
            sectionDividers={props.sectionDividers}
            sections={props.sections}
          />
        ) : (
          props.options.map((option, index) =>
            option === SELECT_DIVIDER ? (
              <Divider key={`${option.key}-${index}`} />
            ) : (
              <RenderOption
                key={option.key}
                compact={props.compact}
                index={index}
                option={option}
              />
            ),
          )
        )}
        {props.options.length === 0 && props.dropdownPlaceholder}
      </div>
    </ListboxOptions>
  );
});

const Divider: React.FC = () => (
  <hr
    className="my-2.5 h-px w-full border-none bg-gray-200"
    data-loc="select-section-divider"
  />
);

export const SELECT_DIVIDER: Option = {
  key: "divider",
  value: <hr />,
  disabled: true,
};
